import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { AxiosError } from 'axios'
import {
  KitchenBoardQueryType,
  KitchenBoardType,
  KitchenFinishRequestType,
  KitchenManualInputRequestType,
  KitchenOrderCardType,
  KitchenOrderDetailType,
  KitchenResetRequestType,
  KitchenRejectRequestType,
  KitchenScanRequestType,
  KitchenTransitionRequestType,
} from 'features/Order/@types/KitchenMonitoring'
import { kitchenMonitoringService } from 'features/Order/services/kitchenMonitoring'
import { callErrorMsg } from 'helpers/errorMsg'
import { canTransitionKitchenOrder } from 'utils/helpers/kitchenTransition'

type RequestStateType = 'idle' | 'loading' | 'succeeded' | 'failed'
type OptimisticTransitionType = {
  order: KitchenOrderCardType
  sourceIndex: number
  targetStatus: KitchenOrderCardType['status']
  search: string
}

export type KitchenMonitoringStateType = {
  board: KitchenBoardType | null
  detail: KitchenOrderDetailType | null
  boardRequest: RequestStateType
  boardRequestId: string | null
  boardSearch: string
  detailRequest: RequestStateType
  detailRequestId: string | null
  pendingOrderIds: string[]
  optimisticTransitions: Record<string, OptimisticTransitionType>
  boardError: string | null
  detailError: string | null
  mutationError: string | null
}

const initialState: KitchenMonitoringStateType = {
  board: null,
  detail: null,
  boardRequest: 'idle',
  boardRequestId: null,
  boardSearch: '',
  detailRequest: 'idle',
  detailRequestId: null,
  pendingOrderIds: [],
  optimisticTransitions: {},
  boardError: null,
  detailError: null,
  mutationError: null,
}

const getError = (error: unknown) => {
  const apiError = error as AxiosError<{ message?: string }>
  if (axios.isAxiosError(error) && apiError.response?.data.message)
    return apiError.response.data.message
  return error instanceof Error ? error.message : 'Aksi Kitchen Monitoring gagal'
}

export const fetchKitchenBoard = createAsyncThunk<
  KitchenBoardType,
  KitchenBoardQueryType,
  { rejectValue: string }
>('kitchenMonitoring/fetchBoard', async (query, { rejectWithValue }) => {
  try {
    return await kitchenMonitoringService.getBoard(query)
  } catch (error) {
    callErrorMsg(error, getError(error))
    return rejectWithValue(getError(error))
  }
})

export const fetchKitchenDetail = createAsyncThunk<
  KitchenOrderDetailType,
  { locationId: string; orderId: string },
  { rejectValue: string }
>('kitchenMonitoring/fetchDetail', async ({ locationId, orderId }, { rejectWithValue }) => {
  try {
    return await kitchenMonitoringService.getDetail(locationId, orderId)
  } catch (error) {
    callErrorMsg(error, getError(error))
    return rejectWithValue(getError(error))
  }
})

const createMutation = <T>(name: string, run: (request: T) => Promise<KitchenOrderDetailType>) =>
  createAsyncThunk<KitchenOrderDetailType, T, { rejectValue: string }>(
    `kitchenMonitoring/${name}`,
    async (request, { rejectWithValue }) => {
      try {
        return await run(request)
      } catch (error) {
        return rejectWithValue(getError(error))
      }
    },
  )

export const transitionKitchenOrder = createMutation<KitchenTransitionRequestType>(
  'transition',
  (request) => kitchenMonitoringService.transition(request),
)
export const scanKitchenItem = createMutation<KitchenScanRequestType>('scan', (request) =>
  kitchenMonitoringService.scan(request),
)
export const manualInputKitchenItem = createMutation<KitchenManualInputRequestType>(
  'manualInput',
  (request) => kitchenMonitoringService.manualInput(request),
)
export const rejectKitchenItem = createMutation<KitchenRejectRequestType>('rejectItem', (request) =>
  kitchenMonitoringService.rejectItem(request),
)
export const resetKitchenItem = createMutation<KitchenResetRequestType>('resetItem', (request) =>
  kitchenMonitoringService.resetItem(request),
)
export const finishKitchenOrder = createMutation<KitchenFinishRequestType>('finish', (request) =>
  kitchenMonitoringService.finish(request),
)

const kitchenMonitoring = createSlice({
  name: 'kitchenMonitoring',
  initialState,
  reducers: {
    clearKitchenMonitoringError(state) {
      state.boardError = null
      state.detailError = null
      state.mutationError = null
    },
    clearKitchenMonitoringDetail(state) {
      state.detail = null
      state.detailRequest = 'idle'
      state.detailRequestId = null
      state.detailError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchKitchenBoard.pending, (state, action) => {
        state.boardRequest = 'loading'
        state.boardRequestId = action.meta.requestId
        state.boardError = null
        state.mutationError = null
      })
      .addCase(fetchKitchenBoard.fulfilled, (state, action) => {
        if (state.boardRequestId !== action.meta.requestId) return
        state.boardRequest = 'succeeded'
        state.boardRequestId = null
        state.boardSearch = action.meta.arg.search || ''
        state.board = action.payload
      })
      .addCase(fetchKitchenBoard.rejected, (state, action) => {
        if (state.boardRequestId !== action.meta.requestId) return
        state.boardRequest = 'failed'
        state.boardRequestId = null
        state.boardError = action.payload || 'Board gagal dimuat'
      })
      .addCase(fetchKitchenDetail.pending, (state, action) => {
        state.detailRequest = 'loading'
        state.detailRequestId = action.meta.requestId
        state.detailError = null
      })
      .addCase(fetchKitchenDetail.fulfilled, (state, action) => {
        if (state.detailRequestId !== action.meta.requestId) return
        state.detailRequest = 'succeeded'
        state.detailRequestId = null
        state.detail = action.payload
      })
      .addCase(fetchKitchenDetail.rejected, (state, action) => {
        if (state.detailRequestId !== action.meta.requestId) return
        state.detailRequest = 'failed'
        state.detailRequestId = null
        state.detailError = action.payload || 'Detail gagal dimuat'
      })
      .addCase(transitionKitchenOrder.pending, (state, action) => {
        const { orderId, locationId, targetStatus } = action.meta.arg
        if (!state.board || state.board.locationId !== locationId) return
        const source = state.board.columns.find((column) =>
          column.orders.some((order) => order.id === orderId),
        )
        const target = state.board.columns.find((column) => column.status === targetStatus)
        const sourceIndex = source?.orders.findIndex((order) => order.id === orderId) ?? -1
        if (
          !source ||
          !target ||
          sourceIndex < 0 ||
          !canTransitionKitchenOrder(source.status, targetStatus)
        )
          return
        const order = { ...source.orders[sourceIndex] }
        state.optimisticTransitions[orderId] = {
          order,
          sourceIndex,
          targetStatus,
          search: state.boardSearch,
        }
        source.orders.splice(sourceIndex, 1)
        source.total -= 1
        target.orders.unshift({ ...order, status: targetStatus })
        target.total += 1
      })
      .addCase(transitionKitchenOrder.fulfilled, (state, action) => {
        const snapshot = state.optimisticTransitions[action.meta.arg.orderId]
        if (
          snapshot &&
          state.board?.locationId === action.meta.arg.locationId &&
          state.boardSearch === snapshot.search
        ) {
          const source = state.board.columns.find(
            (column) => column.status === snapshot.order.status,
          )
          const target = state.board.columns.find(
            (column) => column.status === snapshot.targetStatus,
          )
          const sourceIndex =
            source?.orders.findIndex((order) => order.id === action.payload.id) ?? -1
          if (
            source &&
            target &&
            sourceIndex >= 0 &&
            !target.orders.some((order) => order.id === action.payload.id)
          ) {
            source.orders.splice(sourceIndex, 1)
            source.total -= 1
            target.orders.unshift({ ...snapshot.order, status: snapshot.targetStatus })
            target.total += 1
          }
          const card = target?.orders.find((order) => order.id === action.payload.id)
          if (card) card.version = action.payload.version
        }
        delete state.optimisticTransitions[action.meta.arg.orderId]
      })
      .addCase(transitionKitchenOrder.rejected, (state, action) => {
        const { orderId, locationId } = action.meta.arg
        const snapshot = state.optimisticTransitions[orderId]
        if (
          snapshot &&
          state.board?.locationId === locationId &&
          state.boardSearch === snapshot.search
        ) {
          const source = state.board.columns.find(
            (column) => column.status === snapshot.order.status,
          )
          const target = state.board.columns.find(
            (column) => column.status === snapshot.targetStatus,
          )
          const targetIndex = target?.orders.findIndex((order) => order.id === orderId) ?? -1
          if (target && targetIndex >= 0) {
            target.orders.splice(targetIndex, 1)
            target.total -= 1
          }
          if (source && !source.orders.some((order) => order.id === orderId)) {
            source.orders.splice(snapshot.sourceIndex, 0, snapshot.order)
            source.total += 1
          }
        }
        delete state.optimisticTransitions[orderId]
      })
    ;[
      transitionKitchenOrder,
      scanKitchenItem,
      manualInputKitchenItem,
      rejectKitchenItem,
      resetKitchenItem,
      finishKitchenOrder,
    ].forEach((mutation) => {
      builder.addMatcher(mutation.pending.match, (state, action) => {
        state.mutationError = null
        if (!state.pendingOrderIds.includes(action.meta.arg.orderId))
          state.pendingOrderIds.push(action.meta.arg.orderId)
      })
      builder.addMatcher(mutation.fulfilled.match, (state, action) => {
        state.pendingOrderIds = state.pendingOrderIds.filter((id) => id !== action.meta.arg.orderId)
        if (state.detail?.id === action.payload.id) state.detail = action.payload
      })
      builder.addMatcher(mutation.rejected.match, (state, action) => {
        state.pendingOrderIds = state.pendingOrderIds.filter((id) => id !== action.meta.arg.orderId)
        state.mutationError = action.payload || 'Aksi Kitchen Monitoring gagal'
      })
    })
  },
})

export const { clearKitchenMonitoringError, clearKitchenMonitoringDetail } =
  kitchenMonitoring.actions
export default kitchenMonitoring.reducer
